import React from 'react';
import { Button, Card, Input, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import { useRecruitList } from './shared/useRecruitList';
import { exportRows, fmtCount, fmtDate, fmtMoney, stageLabel } from './shared/recruitUi';

const { Text } = Typography;
const FILTERS = ['status', 'department_id'] as const;
const STATUSES = ['Draft', 'PendingApproval', 'Rejected', 'Sent', 'Accepted', 'Declined', 'Expired', 'Withdrawn', 'Converted'];

const OffersPage: React.FC = () => {
  const navigate = useNavigate();
  const list = useRecruitList(FILTERS);
  const { data, isFetching } = useQuery({
    queryKey: hrmsKeys.offers(list.apiParams),
    queryFn: () => hrmsService.pageOffers(list.apiParams),
    placeholderData: keepPreviousData,
  });

  const exportAll = async () => {
    const all = await hrmsService.pageOffers({ ...list.apiParams, page: 1, limit: 5000 });
    await exportRows(
      'offers',
      [
        ['offer_number', 'Offer #'],
        ['candidate_number', 'Candidate #'],
        ['candidate_name', 'Candidate'],
        ['job_title', 'Role'],
        ['department_name', 'Department'],
        ['currency', 'Currency'],
        ['basic_salary', 'Basic'],
        ['join_date', 'Join date'],
        ['status', 'Status'],
        ['offer_expires_at', 'Expires'],
        ['candidate_responded_at', 'Responded'],
        ['created_at', 'Created'],
      ],
      all.rows
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Offer #',
      dataIndex: 'offer_number',
      width: 140,
      fixed: 'left',
      render: (v: string, r) => <a onClick={() => navigate(`/hr/offers/${r.id}`)}>{v}</a>,
    },
    { title: 'Candidate', dataIndex: 'candidate_name', ellipsis: true },
    { title: 'Role', dataIndex: 'job_title', ellipsis: true },
    { title: 'Department', dataIndex: 'department_name', width: 160, ellipsis: true },
    {
      title: 'Basic',
      dataIndex: 'basic_salary',
      width: 140,
      align: 'right',
      render: (v, r) => fmtMoney(v, r.currency),
    },
    { title: 'Join date', dataIndex: 'join_date', key: 'join_date', width: 120, sorter: true, sortOrder: list.sortOrderFor('join_date'), render: (v) => fmtDate(v) },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 150,
      render: (s: string, r) => (
        <Space size={4}>
          <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag>
          {s === 'Sent' && r.offer_expires_at && dayjs(r.offer_expires_at).diff(dayjs(), 'day') <= 2 && <Tag color="orange">Expiring</Tag>}
        </Space>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Offers"
          subtitle="Approve, send to the candidate, convert accepted offers to employees"
          primaryActions={
            <Space>
              <Button icon={<DownloadOutlined />} onClick={exportAll}>
                Export
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/offers/new')}>
                New offer
              </Button>
            </Space>
          }
        />
      }
    >
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input.Search allowClear placeholder="Offer, candidate, role" defaultValue={list.q} onSearch={list.setQ} style={{ width: 240 }} />
          <Select
            allowClear
            mode="multiple"
            placeholder="Status"
            style={{ minWidth: 220 }}
            value={list.filters.status ? list.filters.status.split(',') : []}
            onChange={(v: string[]) => list.setFilter('status', v.join(',') || undefined)}
            options={STATUSES.map((v) => ({ value: v, label: stageLabel(v) }))}
          />
          <DepartmentSelect
            allowClear
            placeholder="Department"
            style={{ width: 220 }}
            value={list.filters.department_id}
            onChange={(v) => list.setFilter('department_id', v)}
          />
          <Text type="secondary">{fmtCount(data?.total ?? 0)} offer(s)</Text>
        </Space>
      </Card>
      <Table
        rowKey="id"
        loading={isFetching}
        columns={columns}
        dataSource={data?.rows || []}
        scroll={{ x: 1000 }}
        pagination={list.pagination(data?.total || 0)}
        onChange={list.onTableChange}
      />
    </WorkspaceLayout>
  );
};

export default OffersPage;
